import { FC, useCallback, useRef, useState } from 'react';
import { MAX_IMPORT_ROWS, useOrderImport } from 'hooks/use-order-import';
import { downloadUnitOrderImportTemplate } from 'services/unit-orders';
import { UnitOrderImportOutcome, UnitOrderImportRowResult } from 'types';

import './unit-order.scss';

const OUTCOME_LABEL: Record<UnitOrderImportOutcome, string> = {
  CREATED: 'Create',
  UPDATED: 'Update',
  DELETED: 'Delete',
  FAILED: 'Failed'
};

const ImportCounts: FC<{ created: number; updated: number; deleted: number; failed: number }> = ({
  created,
  updated,
  deleted,
  failed
}) => (
  <div className="import-counts" data-cy="order-import-counts">
    <span>Create: {created}</span>
    <span>Update: {updated}</span>
    <span>Delete: {deleted}</span>
    <span className={failed > 0 ? 'text-danger' : ''}>Failed: {failed}</span>
  </div>
);

const ImportRowTable: FC<{ rows: UnitOrderImportRowResult[] }> = ({ rows }) => (
  <div className="import-table-wrapper">
    <table className="import-table" data-cy="order-import-table">
      <thead>
        <tr>
          <th>Row</th>
          <th>Account</th>
          <th>Unit Number</th>
          <th>Notes</th>
          <th>Action</th>
          <th>Outcome</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr
            key={row.rowNumber}
            className={row.requestedAction === 'DELETE' ? 'import-row-delete' : undefined}
            data-cy={`order-import-row-${row.rowNumber}`}
            data-testid={`order-import-row-${row.rowNumber}`}
          >
            <td>{row.rowNumber}</td>
            <td>{row.account}</td>
            <td>{row.unitNumber}</td>
            <td>{row.notes}</td>
            <td>{row.requestedAction}</td>
            <td className={row.outcome === 'FAILED' ? 'text-danger' : undefined}>
              {row.reason ? `${OUTCOME_LABEL[row.outcome]} — ${row.reason}` : OUTCOME_LABEL[row.outcome]}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ImportErrors: FC<{ rows: UnitOrderImportRowResult[] }> = ({ rows }) => {
  const failed = rows.filter(row => row.outcome === 'FAILED');
  if (failed.length === 0) return null;
  return (
    <div className="import-errors" data-cy="order-import-errors" data-testid="order-import-errors">
      <h4 className="text-danger">{failed.length} row(s) failed</h4>
      <ul>
        {failed.map(row => (
          <li key={row.rowNumber}>
            Row {row.rowNumber} ({row.account} / {row.unitNumber}): {row.reason}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ImportOrders: FC<{ handleClose: () => void; onImported: () => void }> = ({ handleClose, onImported }) => {
  const { file, preview, result, loading, error, selectFile, reset, commit, retryFailed } = useOrderImport();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState(false);

  const failedRows = result?.rows.filter(row => row.outcome === 'FAILED') ?? [];
  const canImport = !!file && !!preview && preview.failed === 0 && !loading;

  const handleFileChange = useCallback(
    (selected: File | null) => {
      setConfirming(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      void selectFile(selected);
    },
    [selectFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const dropped = event.dataTransfer.files?.[0];
      if (dropped) handleFileChange(dropped);
    },
    [handleFileChange]
  );

  const handleImport = useCallback(async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    const response = await commit();
    setConfirming(false);
    if (response) onImported();
  }, [confirming, commit, onImported]);

  const handleCancel = useCallback(() => {
    reset();
    handleClose();
  }, [reset, handleClose]);

  const handleRetry = useCallback(async () => {
    setConfirming(false);
    await retryFailed();
  }, [retryFailed]);

  return (
    <div className="import-orders">
      <h3 className="font-weight-medium text-info-dark">Import Stop / Allow Orders</h3>
      <p className="text-big text-info-main white-space-wrap">
        Upload a UTF-8 CSV with the headers <b>Account</b>, <b>UnitNumber</b>, <b>Notes</b>, and <b>Hold</b>. Account
        may be the customer <b>code</b> or <b>name</b> (for example <b>MSK</b> or <b>PIDGEON DEPOT</b>). Hold must be{' '}
        <b>Stop</b>, <b>Allow</b>, or <b>Delete</b>. Delete rows are highlighted and remove only the exact account/unit
        match. Limits: 10 MB and {MAX_IMPORT_ROWS.toLocaleString()} rows.
      </p>

      <div className="d-flex align-items-center g-2">
        <button
          className="btn-outline"
          type="button"
          onClick={downloadUnitOrderImportTemplate}
          data-cy="order-import-template"
        >
          download template
        </button>
      </div>

      <div
        className="import-dropzone"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={event => event.preventDefault()}
        onDrop={handleDrop}
        data-cy="order-import-dropzone"
      >
        <span className="material-icons icon-xlarge">cloud_upload</span>
        <p>{file ? file.name : 'Drop a CSV file here or click to browse'}</p>
        {preview && <p>{preview.totalRows} data row(s)</p>}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={event => handleFileChange(event.target.files?.[0] ?? null)}
        data-cy="order-import-file-input"
        data-testid="order-import-file-input"
      />

      {error && <p className="text-danger">{error}</p>}
      {loading && <p className="text-info-main">Processing…</p>}

      {preview && !result && (
        <>
          <ImportCounts
            created={preview.created}
            updated={preview.updated}
            deleted={preview.deleted}
            failed={preview.failed}
          />
          {preview.failed > 0 && (
            <p className="text-danger">
              {preview.failed} row(s) have errors. Fix the file and re-upload before importing.
            </p>
          )}
          <ImportErrors rows={preview.rows} />
          <ImportRowTable rows={preview.rows} />
        </>
      )}

      {result && (
        <>
          <ImportCounts
            created={result.created}
            updated={result.updated}
            deleted={result.deleted}
            failed={result.failed}
          />
          <ImportErrors rows={result.rows} />
          <ImportRowTable rows={result.rows} />
          {failedRows.length > 0 && (
            <div className="d-flex align-items-center g-2">
              <button className="btn-outline" type="button" onClick={handleRetry} data-cy="order-import-retry">
                retry failed rows
              </button>
            </div>
          )}
        </>
      )}

      {confirming ? (
        <div className="d-flex align-items-center g-2 mt-3" style={{ justifyContent: 'flex-end' }}>
          <span className="text-big">
            Create {preview?.created ?? 0}, update {preview?.updated ?? 0}, delete {preview?.deleted ?? 0} order(s).
            Continue?
          </span>
          <button className="btn-outline" type="button" onClick={() => setConfirming(false)} disabled={loading}>
            back
          </button>
          <button
            className="btn-danger"
            type="button"
            onClick={handleImport}
            disabled={loading}
            data-cy="order-import-confirm"
          >
            confirm import
          </button>
        </div>
      ) : (
        <div className="d-flex align-items-center g-2 mt-3" style={{ justifyContent: 'flex-end' }}>
          <button
            className="btn-outline"
            type="button"
            onClick={handleCancel}
            disabled={loading}
            data-cy="order-import-cancel"
          >
            cancel
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={handleImport}
            disabled={!canImport}
            data-cy="order-import-submit"
          >
            import
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportOrders;
